import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openDialog } from '../../../base/dialog/actions';
import { isLocalParticipantModerator } from '../../../base/participants/functions';
import { isInBreakoutRoom } from '../../../breakout-rooms/functions';
import { amIInBackstage } from '../../../webinar/functions';
import { toggleVisitorsMode } from '../../actions';
import { getPromotionRequests, getVisitorModeToggleError, getVisitorsToggleValue, isVisitorModeSupported, isVisitorModeTogglePending } from '../../functions';
import EnableVisitorsDialog from './EnableVisitorsDialog';
/**
 * Shared moderator visitor-mode toggle logic, used by both the toolbar dialog and the participants
 * pane toggle. Turning visitor mode on opens the apply-mode dialog; turning it off disables directly.
 *
 * @returns {IVisitorsToggle}
 */
export function useVisitorsToggle() {
    var _a, _b, _c;
    const dispatch = useDispatch();
    const conference = useSelector((state) => state['features/base/conference'].conference);
    const isModerator = useSelector(isLocalParticipantModerator);
    const inBreakoutRoom = useSelector(isInBreakoutRoom);
    const inBackstage = useSelector(amIInBackstage);
    const supported = useSelector(isVisitorModeSupported);
    const toggleValue = useSelector(getVisitorsToggleValue);
    const togglePending = useSelector(isVisitorModeTogglePending);
    const toggleError = useSelector(getVisitorModeToggleError);
    const visitorsCount = useSelector((state) => state['features/visitors'].count || 0);
    const requests = useSelector(getPromotionRequests);
    const metadataSupported = Boolean((_c = (_b = (_a = conference === null || conference === void 0 ? void 0 : conference.getMetadataHandler) === null || _a === void 0 ? void 0 : _a.call(conference)) === null || _b === void 0 ? void 0 : _b.isSupported) === null || _c === void 0 ? void 0 : _c.call(_b));
    // Backstage is a separate room. Visitor mode is a property of the main
    // meeting, and the metadata write from here would arm the backstage room
    // instead, so the control is hidden rather than made to fail silently.
    const visible = Boolean(conference) && isModerator && !inBreakoutRoom && !inBackstage
        && (supported || metadataSupported);
    const onToggleVisitors = useCallback(() => {
        if (togglePending) {
            return;
        }
        if (toggleValue) {
            // Currently on - turn it off directly.
            dispatch(toggleVisitorsMode(false));
        }
        else {
            // Currently off - ask how it should apply before enabling.
            dispatch(openDialog(EnableVisitorsDialog));
        }
    }, [dispatch, toggleValue, togglePending]);
    return {
        onToggleVisitors,
        requests,
        toggleError,
        toggleValue,
        togglePending,
        visible,
        visitorsCount
    };
}
